import React, {
  useState,
  useEffect,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  Keyboard,
  ActivityIndicator,
} from "react-native";
import { useTranslation } from "react-i18next";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import { getOrFetchMapiSubdivisions } from "../../../../../helper/mapiSubdivisionsCache";

type FieldInfo = {
  field: string;
  dataType: string;
  required?: boolean;
  fieldLabel?: string;
  displayOrder?: string;
  fieldMin?: string;
  fieldMax?: string;
  exampleText?: string;
  enumerationItem?: Array<{ value: string; description: string }>;
};

type Props = {
  onValidationChange?: (isValid: boolean) => void;
  testID?: string | undefined;
};

export interface MapiSenderIDStepHandle {
  submit: () => Promise<boolean>;
  getValues: () => Record<string, string>;
}

//  THIS PAGE WILL DISPLAY ALL FIELDS THAT STARTS WITH sender.primaryIdentification
//  AND REQUIRED IS TRUE

const COUNTRY_CODE_FIELD = "sender.primaryIdentification.issueCountryCode";
const SUBDIVISION_CODE_FIELD = "sender.primaryIdentification.issueCountrySubdivisionCode";

const MapiSenderIDStep = forwardRef<MapiSenderIDStepHandle, Props>(
  ({ onValidationChange, testID }: Props, ref) => {
    const { t } = useTranslation();

    const mapiTransactionFieldsData = useSessionStore(
      (s: SessionState) => (s as any).mapiTransactionFieldsData
    );
    const fieldInfo = useMemo(
      () => (mapiTransactionFieldsData?.fieldInfo ?? []) as FieldInfo[],
      [mapiTransactionFieldsData]
    );

    const mapiCountriesList = useSessionStore(
      (s: SessionState) => (s as any).mapiCountriesList
    ) as any[] | null;

    /** Subdivisions for ID *issue* country only (not destination country from the flow). */
    const [issueSubdivisions, setIssueSubdivisions] = useState<any[]>([]);
    const [issueSubdivisionsLoading, setIssueSubdivisionsLoading] =
      useState(false);
    const prevIssueCountryRef = useRef<string | null>(null);

    const primaryIdFields = useMemo(() => {
      const required = fieldInfo
        .filter((f) => (f.field || "").startsWith("sender.primaryIdentification"))
        .filter((f) => f.required === true);

      const hasSubdivision = required.some((f) => f.field === SUBDIVISION_CODE_FIELD);
      if (!hasSubdivision) {
        const subdivField = fieldInfo.find((f) => f.field === SUBDIVISION_CODE_FIELD);
        if (subdivField) {
          required.push(subdivField);
        }
      }

      return required.sort((a, b) => {
        const orderA = parseInt(a.displayOrder || "999", 10);
        const orderB = parseInt(b.displayOrder || "999", 10);
        return orderA - orderB;
      });
    }, [fieldInfo]);

    const [values, setValues] = useState<Record<string, string>>({});
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
    /** Controlled search text for country / subdivision comboboxes. */
    const [comboDraft, setComboDraft] = useState<Record<string, string>>({});
    const [focusedComboField, setFocusedComboField] = useState<string | null>(null);
    const comboBlurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      return () => {
        if (comboBlurTimerRef.current) clearTimeout(comboBlurTimerRef.current);
      };
    }, []);

    const isValid = useMemo(() => {
      return primaryIdFields.every((f) => {
        const v = (values[f.field] || "").trim();
        return v.length > 0;
      });
    }, [primaryIdFields, values]);

    useEffect(() => {
      onValidationChange?.(isValid);
    }, [isValid, onValidationChange]);

    useEffect(() => {
      const iso = (values[COUNTRY_CODE_FIELD] || "").trim();

      if (
        prevIssueCountryRef.current !== null &&
        prevIssueCountryRef.current !== iso
      ) {
        setValues((v) => ({ ...v, [SUBDIVISION_CODE_FIELD]: "" }));
        setComboDraft((c) => {
          const next = { ...c };
          delete next[SUBDIVISION_CODE_FIELD];
          return next;
        });
        setErrors((e) => ({ ...e, [SUBDIVISION_CODE_FIELD]: "" }));
      }
      prevIssueCountryRef.current = iso || null;

      if (!iso) {
        setIssueSubdivisions([]);
        setIssueSubdivisionsLoading(false);
        return;
      }

      const cacheKey = iso.toUpperCase();
      const cached = (useSessionStore.getState() as any).mapiSubdivisionsByCountryCode?.[
        cacheKey
      ];
      if (cached !== undefined) {
        setIssueSubdivisions(cached);
        setIssueSubdivisionsLoading(false);
        return;
      }

      let cancelled = false;
      setIssueSubdivisionsLoading(true);
      getOrFetchMapiSubdivisions(iso).then((list) => {
        if (cancelled) return;
        setIssueSubdivisions(list);
        setIssueSubdivisionsLoading(false);
      });

      return () => {
        cancelled = true;
      };
    }, [values[COUNTRY_CODE_FIELD]]);

    const updateValue = (field: string, value: string) => {
      setValues((prev) => ({ ...prev, [field]: value }));
      setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    useImperativeHandle(ref, () => ({
      submit: async () => {
        const newErrors: Record<string, string> = {};
        primaryIdFields.forEach((f) => {
          const v = (values[f.field] || "").trim();
          if (!v) {
            newErrors[f.field] = `${f.fieldLabel || f.field} is required`;
          }
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
      },
      getValues: () => ({ ...values }),
    }));

    const getDropdownItems = (
      f: FieldInfo
    ): Array<{ value: string; description: string }> | null => {
      if (f.field === COUNTRY_CODE_FIELD && Array.isArray(mapiCountriesList) && mapiCountriesList.length > 0) {
        return mapiCountriesList.map((c: any) => ({
          value: c.iso3Code || "",
          description: c.name || c.iso3Code || "",
        }));
      }
      if (f.field === SUBDIVISION_CODE_FIELD) {
        if (!(values[COUNTRY_CODE_FIELD] || "").trim()) {
          return null;
        }
        if (!Array.isArray(issueSubdivisions) || issueSubdivisions.length === 0) {
          return null;
        }
        return issueSubdivisions.map((s: any) => ({
          value: s.code || s.iso2Code || "",
          description: s.name || s.code || "",
        }));
      }
      if (Array.isArray(f.enumerationItem) && f.enumerationItem.length > 0) {
        return f.enumerationItem;
      }
      return null;
    };

    const isSearchableComboField = (field: string) =>
      field === COUNTRY_CODE_FIELD || field === SUBDIVISION_CODE_FIELD;

    const filterComboItems = (
      items: Array<{ value: string; description: string }>,
      query: string
    ) => {
      const q = query.trim().toLowerCase();
      if (!q) return items.slice(0, 80);
      return items.filter(
        (i) =>
          i.description.toLowerCase().includes(q) ||
          i.value.toLowerCase().includes(q)
      );
    };

    const renderSearchableSelect = (
      f: FieldInfo,
      items: Array<{ value: string; description: string }>
    ) => {
      const label = f.fieldLabel || f.field;
      const value = values[f.field] || "";
      const error = errors[f.field] || "";
      const selectedItem = items.find((e) => e.value === value);
      const draft = comboDraft[f.field];
      const inputValue =
        draft !== undefined ? draft : selectedItem?.description ?? "";
      const filtered = filterComboItems(items, inputValue);
      const showList =
        focusedComboField === f.field && filtered.length > 0;

      const scheduleComboBlur = () => {
        if (comboBlurTimerRef.current) clearTimeout(comboBlurTimerRef.current);
        comboBlurTimerRef.current = setTimeout(() => {
          comboBlurTimerRef.current = null;
          setFocusedComboField(null);
        }, 200);
      };

      const onComboChangeText = (text: string) => {
        setComboDraft((prev) => ({ ...prev, [f.field]: text }));
        const sel = items.find((e) => e.value === values[f.field]);
        const matchesSelection =
          !!sel &&
          (text === sel.description ||
            text.trim().toUpperCase() === sel.value.trim().toUpperCase());
        if (matchesSelection) {
          updateValue(f.field, sel!.value);
          return;
        }
        const tq = text.trim();
        if (!tq) {
          updateValue(f.field, "");
          return;
        }
        const exactByDesc = items.find(
          (i) => i.description.trim().toLowerCase() === tq.toLowerCase()
        );
        const exactByCode = items.find(
          (i) => i.value.trim().toLowerCase() === tq.toLowerCase()
        );
        const exact = exactByDesc || exactByCode;
        if (exact) {
          updateValue(f.field, exact.value);
          setComboDraft((prev) => ({ ...prev, [f.field]: exact.description }));
          return;
        }
        updateValue(f.field, "");
      };

      const pickItem = (item: { value: string; description: string }) => {
        if (comboBlurTimerRef.current) clearTimeout(comboBlurTimerRef.current);
        updateValue(f.field, item.value);
        setComboDraft((prev) => ({ ...prev, [f.field]: item.description }));
        setFocusedComboField(null);
        Keyboard.dismiss();
      };

      return (
        <View key={f.field} style={styles.fieldWrapper}>
          <FloatingTextInputComponent
            testID={`mapi-senderId-${f.field}`}
            label={label}
            value={inputValue}
            onChangeText={onComboChangeText}
            errorText={error}
            autoCapitalize="none"
            autoCorrect={false}
            spellCheck={false}
            onFocus={() => {
              if (comboBlurTimerRef.current) clearTimeout(comboBlurTimerRef.current);
              setDropdownOpen(null);
              setFocusedComboField(f.field);
            }}
            onBlur={scheduleComboBlur}
          />
          {showList && (
            <View style={styles.dropdownContainer}>
              <FlatList
                keyboardShouldPersistTaps="handled"
                data={filtered}
                keyExtractor={(item) => item.value}
                nestedScrollEnabled
                renderItem={({ item }) => (
                  <TouchableOpacity
                    testID={`mapi-senderId-option-${item.value}`}
                    style={styles.suggestionItem}
                    onPressIn={() => {
                      if (comboBlurTimerRef.current) {
                        clearTimeout(comboBlurTimerRef.current);
                        comboBlurTimerRef.current = null;
                      }
                    }}
                    onPress={() => pickItem(item)}
                  >
                    <Text style={styles.suggestionText}>{item.description}</Text>
                    <Text style={styles.suggestionSub}>{item.value}</Text>
                  </TouchableOpacity>
                )}
              />
            </View>
          )}
          {!!error && <Text style={styles.errorText}>{error}</Text>}
        </View>
      );
    };

    const renderDropdown = (
      f: FieldInfo,
      items: Array<{ value: string; description: string }>
    ) => {
      const label = f.fieldLabel || f.field;
      const value = values[f.field] || "";
      const error = errors[f.field] || "";
      const selectedItem = items.find((e) => e.value === value);
      const displayValue = selectedItem?.description ?? "";

      return (
        <View key={f.field} style={styles.fieldWrapper}>
          <TouchableOpacity
            testID={`mapi-senderId-${f.field}`}
            style={[styles.dropdownTrigger, error ? styles.dropdownError : null]}
            onPress={() => {
              setDropdownOpen(dropdownOpen === f.field ? null : f.field);
              Keyboard.dismiss();
            }}
          >
            <Text
              style={[styles.dropdownText, !displayValue && styles.placeholderText]}
              numberOfLines={1}
            >
              {displayValue || `Select ${label}`}
            </Text>
            <Text style={styles.dropdownArrow}>▼</Text>
          </TouchableOpacity>
          {dropdownOpen === f.field && (
            <View style={styles.dropdownContainer}>
              <FlatList
                keyboardShouldPersistTaps="handled"
                data={items}
                keyExtractor={(item) => item.value}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    testID={`mapi-senderId-option-${item.value}`}
                    style={styles.suggestionItem}
                    onPress={() => {
                      updateValue(f.field, item.value);
                      setDropdownOpen(null);
                    }}
                  >
                    <Text style={styles.suggestionText}>{item.description}</Text>
                  </TouchableOpacity>
                )}
              />
            </View>
          )}
          {!!error && <Text style={styles.errorText}>{error}</Text>}
        </View>
      );
    };

    const renderField = (f: FieldInfo) => {
      const label = f.fieldLabel || f.field;
      const value = values[f.field] || "";
      const error = errors[f.field] || "";
      const maxLen = f.fieldMax ? parseInt(f.fieldMax, 10) : undefined;

      if (f.field === SUBDIVISION_CODE_FIELD) {
        const issueCountry = (values[COUNTRY_CODE_FIELD] || "").trim();
        if (!issueCountry) {
          return (
            <View key={f.field} style={styles.fieldWrapper}>
              <FloatingTextInputComponent
                testID={`mapi-senderId-${f.field}`}
                label={label}
                value=""
                onChangeText={() => {}}
                editable={false}
                errorText={error}
              />
              <Text style={styles.hintText}>
                {t("SENDER_ID_SELECT_ISSUE_COUNTRY_FIRST") ||
                  "Select the ID issue country first to load states or provinces."}
              </Text>
            </View>
          );
        }
        if (issueSubdivisionsLoading) {
          return (
            <View key={f.field} style={styles.fieldWrapper}>
              <Text style={styles.loadingLabel}>{label}</Text>
              <View style={styles.loadingRow}>
                <ActivityIndicator size="small" color={Colors.titleTextColor} />
                <Text style={[styles.hintText, styles.loadingHintText]}>
                  {t("SENDER_ID_LOADING_SUBDIVISIONS") ||
                    "Loading states or provinces…"}
                </Text>
              </View>
            </View>
          );
        }
      }

      const dropdownItems = getDropdownItems(f);
      if (dropdownItems && dropdownItems.length > 0) {
        if (isSearchableComboField(f.field)) {
          return renderSearchableSelect(f, dropdownItems);
        }
        return renderDropdown(f, dropdownItems);
      }

      const placeholder = f.exampleText || label;
      const keyboardType =
        f.dataType === "gYear" || f.dataType === "gMonth" || f.dataType === "gDay"
          ? "number-pad"
          : "default";

      return (
        <View key={f.field} style={styles.fieldWrapper}>
          <FloatingTextInputComponent
            testID={`mapi-senderId-${f.field}`}
            label={label}
            value={value}
            onChangeText={(v) => updateValue(f.field, v)}
            errorText={error}
            maxLength={maxLen}
            keyboardType={keyboardType as any}
          />
        </View>
      );
    };

    if (primaryIdFields.length === 0) {
      return (
        <View style={styles.container} testID={testID}>
          <Text style={styles.title}>{t("SENDER_ID_TITLE") || "Sender ID"}</Text>
          <Text style={styles.subtitle}>
            {t("SENDER_ID_NO_FIELDS") || "No sender ID fields required for this transaction."}
          </Text>
        </View>
      );
    }

    return (
      <View style={styles.container} testID={testID}>
        <Text style={styles.title}>{t("SENDER_ID_TITLE") || "Sender Identification"}</Text>
        <Text style={styles.subtitle}>
          {t("SENDER_ID_SUBTITLE") || "Please enter your ID details as shown on your document."}
        </Text>

        {primaryIdFields.map((f) => renderField(f))}
      </View>
    );
  }
);

export default MapiSenderIDStep;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: "400",
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24,
  },
  fieldWrapper: {
    marginBottom: 16,
  },
  dropdownTrigger: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: Colors.textInputBackground,
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 12,
    minHeight: 56,
    borderWidth: 1,
    borderColor: Colors.borderGrey,
  },
  dropdownError: {
    borderColor: Colors.errorRedColor,
    borderWidth: 1,
  },
  dropdownText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 16,
    fontWeight: "400",
    color: Colors.titleTextColor,
    flex: 1,
  },
  placeholderText: {
    color: Colors.subTitleColor,
  },
  dropdownArrow: {
    fontSize: 10,
    color: Colors.subTitleColor,
    marginLeft: 8,
  },
  dropdownContainer: {
    maxHeight: 200,
    borderWidth: 1,
    borderColor: Colors.borderGrey,
    borderRadius: 8,
    backgroundColor: Colors.white,
    marginTop: 4,
  },
  suggestionItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderColor: Colors.borderGrey,
    backgroundColor: Colors.white,
  },
  suggestionText: {
    fontSize: 16,
    fontFamily: ComponentStyles.textFamily,
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 22,
  },
  suggestionSub: {
    fontSize: 12,
    fontFamily: ComponentStyles.textFamily,
    color: Colors.subTitleColor,
    marginTop: 2,
  },
  errorText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    color: Colors.errorRedColor,
    marginTop: 4,
  },
  hintText: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 13,
    color: Colors.subTitleColor,
    marginTop: 8,
    lineHeight: 18,
  },
  loadingLabel: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    color: Colors.infoSubValue,
    marginBottom: 6,
  },
  loadingRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  loadingHintText: {
    marginLeft: 10,
    marginTop: 0,
  },
});
